/* eslint-disable react-hooks/exhaustive-deps */
import { Search, RotateCw } from 'lucide-react'
import moment from 'moment'
import { useEffect, useState } from 'react'
import {
  Button,
  Card,
  Col,
  Container,
  Form,
  InputGroup,
  Row,
  Table
} from 'react-bootstrap'
import { Loader } from 'react-bootstrap-typeahead'
import { useLocation, useNavigate } from 'react-router-dom'
import Select from 'react-select'
import RowEmpty from '../../../components/atoms/RowEmpty'
import TableAction from '../../../components/atoms/Table/TableAction'
import TablePagination from '../../../components/atoms/Table/TablePagination'
import InputDatePicker from '../../../components/Input/DatePicker/InputDatePicker'
import ModalCreate from '../../../components/modal/ReceiveAtWarehousePlan/ModalCreate'
import Notification from '../../../components/Notifications/Notification'
import api from '../../../config/AxiosInstance'
import { convertDateWithoutTime } from '../../../helpers/constants'
import * as Common from '../../../utils/Common'
import { firstLetterUpperCase } from '../../../utils/TextFormatter'

const headerTitleStyle = {
  color: 'var(--primary-orange)',
  fontSize: '24px',
  fontWeight: '600'
}

const tableHeaderStyle = {
  fontSize: '14px',
  fontWeight: '600',
  color: '#333333',
  letterSpacing: '0.5px',
  backgroundColor: '#f8f9fa',
  borderBottom: '2px solid #e9ecef'
}

const cardFilterStyle = {
  border: 'none',
  boxShadow: '0 2px 12px rgba(0,0,0,0.04)',
  borderRadius: '12px',
  padding: '1.5rem',
  marginBottom: '1.5rem',
  backgroundColor: '#fff'
}

const labelStyle = {
  fontSize: '0.75rem',
  fontWeight: '700',
  color: '#333333',
  textTransform: 'uppercase',
  marginBottom: '0.5rem',
  letterSpacing: '0.5px'
}

const FormControlStyle = {
  height: '45px',
  borderRadius: '10px',
  border: '1px solid #e9ecef',
  backgroundColor: '#ffffff',
  fontSize: '0.9rem'
}

const SelectStyles = {
  control: (base) => ({
    ...base,
    borderRadius: '10px',
    border: '1px solid #e9ecef',
    boxShadow: 'none',
    minHeight: '45px',
    fontSize: '0.9rem',
    '&:hover': {
      border: '1px solid #bdc3c7'
    }
  }),
  menu: (base) => ({
    ...base,
    borderRadius: '10px',
    fontSize: '0.9rem',
    zIndex: 9999
  })
}

const ReceiveAtWarehousePlan = () => {
  const [show, setShow] = useState(false)
  const navigate = useNavigate()
  const { state } = useLocation()
  const [isLoading, setIsLoading] = useState(false)
  const [notificationSuccess, setNotificationSuccess] = useState(false)
  const [filter, setFilter] = useState({
    src_warehouse_id: state?.filter?.src_warehouse_id || '',
    target_warehouse_id: state?.filter?.target_warehouse_id || '',
    start_date: state?.filter?.start_date || '',
    end_date: state?.filter?.end_date || '',
    tr: state?.filter?.tr || ''
  })
  const [debouncedTr, setDebouncedTr] = useState(filter.tr)

  const [createMl, setCreateMl] = useState({
    mlId: null,
    code: null,
    series_number: null
  })
  const [warehouses, setWarehouses] = useState([])
  const [targetWarehouses, setTargetWarehouses] = useState([])
  const [listData, setListData] = useState([])
  const [meta, setMeta] = useState({
    per_page: 10,
    current_page: 1,
    total_row: 0,
    total_page: 0
  })

  const srcWarehouseOptions = [
    { value: '', label: 'All Source Warehouses' },
    ...warehouses.map((w) => ({ value: w.id, label: w.warehouse_name }))
  ]

  const targetWarehouseOptions = [
    { value: '', label: 'All Target Warehouses' },
    ...targetWarehouses.map((w) => ({ value: w.id, label: w.warehouse_name }))
  ]

  const fetchWarehouses = async () => {
    try {
      const res = await api.get(`/master/warehouses/all`, {
        params: {
          forUser: 1
        }
      })
      const { data } = res
      if (data.success) {
        setWarehouses(data.data)
      }
    } catch (error) {
      const err = error.response.data.errors.message || 'An error occurred'
      Common.showPopUpErrorMessage(err)
    }
  }

  const fetchTargetWarehouses = async () => {
    try {
      const res = await api.get(`/master/warehouses/all`, {
        params: {
          is_parent: true
        }
      })
      const { data } = res
      if (data.success) {
        setTargetWarehouses(data.data)
      }
    } catch (error) {
      const err = error.response.data.errors.message || 'An error occurred'
      Common.showPopUpErrorMessage(err)
    }
  }

  const fetchListData = async () => {
    try {
      setIsLoading(true)
      const params = {
        ...filter,
        tr: debouncedTr,
        per_page: meta.per_page,
        page: meta.current_page
      }
      const res = await api.get('/inbound/ml-plan', { params })
      const { data } = res
      if (data.success) {
        setListData(data.data)
        setMeta(data.metadata)
      } else {
        setListData([])
      }
    } catch (error) {
      if (error?.response?.status !== 404) {
        const err =
          error?.response?.data?.errors?.message ||
          error?.message ||
          'An error occurred'
        Common.showPopUpErrorMessage(err)
      } else {
        setListData([])
      }
    } finally {
      setIsLoading(false)
    }
  }

  const handleChangeInputFilter = (e) => {
    const { name, value } = e.target
    setFilter({
      ...filter,
      [name]: value
    })
  }

  const handleFilterChange = (name, value) => {
    setFilter((prev) => {
      const newFilter = { ...prev, [name]: value }
      if (name === 'start_date' && value && prev.end_date) {
        if (moment(value) > moment(prev.end_date)) {
          newFilter.end_date = value
        }
      } else if (name === 'end_date' && value && prev.start_date) {
        if (moment(value) < moment(prev.start_date)) {
          newFilter.start_date = value
        }
      }
      return newFilter
    })
    setMeta((prev) => ({ ...prev, current_page: 1 }))
  }

  const handleOnChangeMeta = (e) => {
    setMeta((prevMeta) => ({
      ...prevMeta,
      per_page: parseInt(e.target.value, 10),
      current_page: 1
    }))
  }

  const handlePageChange = (data) => {
    setMeta((prevMeta) => ({
      ...prevMeta,
      current_page: data.selected + 1
    }))
    window.scrollTo(0, 0)
  }

  const handleCreateMlDraft = async () => {
    navigate('/arrival/receive-at-warehouse-plan/create', {
      replace: true,
      state: {
        filter: filter,
        form: createMl
      }
    })
  }

  const handleReset = () => {
    setFilter({
      src_warehouse_id: '',
      target_warehouse_id: '',
      start_date: '',
      end_date: '',
      tr: ''
    })
    setDebouncedTr('')
    setMeta((prev) => ({ ...prev, current_page: 1 }))
  }

  useEffect(() => {
    fetchWarehouses()
    fetchTargetWarehouses()
  }, [])

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedTr(filter.tr)
      setMeta((prev) => ({ ...prev, current_page: 1 }))
    }, 1000)
    return () => clearTimeout(handler)
  }, [filter.tr])

  useEffect(() => {
    const isDateValid =
      (!filter.start_date && !filter.end_date) ||
      (filter.start_date && filter.end_date)

    if (isDateValid) {
      fetchListData()
    }
  }, [
    meta.per_page,
    meta.current_page,
    debouncedTr,
    filter.src_warehouse_id,
    filter.target_warehouse_id,
    filter.start_date,
    filter.end_date
  ])

  useEffect(() => {
    if (notificationSuccess) {
      const timer = setTimeout(() => {
        setNotificationSuccess(false)
      }, 10000)
      return () => clearTimeout(timer)
    }
  }, [notificationSuccess])

  return (
    <>
      <Container fluid className="px-4">
        <Row className="mb-4 align-items-center">
          <Col>
            <h3 style={headerTitleStyle}>Receive At Warehouse Plan</h3>
          </Col>
          <Col className="text-end">
            <Button
              variant="light"
              className="text-secondary fw-bold rounded px-3 border"
              onClick={handleReset}
              style={{ fontSize: '0.85rem', height: '37px' }}
            >
              <RotateCw size={16} className="me-2" />
              Reset Filters
            </Button>
          </Col>
          {notificationSuccess && (
            <Notification
              type="success"
              text="ML draft created!"
              handleClose={() => setNotificationSuccess(false)}
              link=""
            />
          )}
        </Row>

        <Card style={cardFilterStyle}>
          <Row className="g-3">
            <Col md={3}>
              <Form.Group>
                <Form.Label style={labelStyle}>Search</Form.Label>
                <InputGroup>
                  <InputGroup.Text
                    style={{
                      backgroundColor: '#f8f9fa',
                      border: '1px solid #e9ecef',
                      borderRight: 'none',
                      borderTopLeftRadius: '10px',
                      borderBottomLeftRadius: '10px'
                    }}
                  >
                    <Search size={16} className="text-muted" />
                  </InputGroup.Text>
                  <Form.Control
                    type="text"
                    name="tr"
                    id="tr"
                    placeholder="Search #TR"
                    value={filter.tr}
                    onChange={(e) => handleChangeInputFilter(e)}
                    style={{
                      ...FormControlStyle,
                      borderLeft: 'none',
                      borderTopLeftRadius: 0,
                      borderBottomLeftRadius: 0
                    }}
                  />
                </InputGroup>
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label style={labelStyle}>Source Warehouse</Form.Label>
                <Select
                  name="src_warehouse_id"
                  id="src_warehouse_id"
                  value={srcWarehouseOptions.find(
                    (o) => o.value === filter.src_warehouse_id
                  )}
                  onChange={(e) =>
                    handleFilterChange('src_warehouse_id', e?.value || '')
                  }
                  options={srcWarehouseOptions}
                  styles={SelectStyles}
                  placeholder="All Source Warehouses"
                />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label style={labelStyle}>Target Warehouse</Form.Label>
                <Select
                  name="target_warehouse_id"
                  id="target_warehouse_id"
                  value={targetWarehouseOptions.find(
                    (o) => o.value === filter.target_warehouse_id
                  )}
                  onChange={(e) =>
                    handleFilterChange('target_warehouse_id', e?.value || '')
                  }
                  options={targetWarehouseOptions}
                  styles={SelectStyles}
                  placeholder="All Target Warehouses"
                />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label style={labelStyle}>Delivery Date</Form.Label>
                <Row className="g-0">
                  <Col
                    xs={6}
                    style={{ paddingRight: '2px', position: 'relative' }}
                  >
                    <div
                      style={FormControlStyle}
                      className="d-flex align-items-center px-1"
                    >
                      <InputDatePicker
                        value={filter?.start_date}
                        onChange={(date) => {
                          handleFilterChange(
                            'start_date',
                            date ? moment(date).format('YYYY-MM-DD') : ''
                          )
                        }}
                        maxDate={
                          filter.end_date ? new Date(filter.end_date) : null
                        }
                        clearIcon
                        placeholder="Start Date"
                      />
                    </div>
                  </Col>

                  <Col xs={6} style={{ paddingLeft: '2px' }}>
                    <div
                      style={FormControlStyle}
                      className="d-flex align-items-center px-1"
                    >
                      <InputDatePicker
                        value={filter?.end_date}
                        onChange={(date) => {
                          handleFilterChange(
                            'end_date',
                            date ? moment(date).format('YYYY-MM-DD') : ''
                          )
                        }}
                        minDate={
                          filter.start_date ? new Date(filter.start_date) : null
                        }
                        clearIcon
                        placeholder="End Date"
                      />
                    </div>
                  </Col>
                </Row>
              </Form.Group>
            </Col>
          </Row>
        </Card>

        <Row className="mt-4">
          <Col md={12}>
            <Table
              hover
              responsive
              className="shadow-sm mb-5 bg-white"
              style={{
                borderRadius: '12px',
                overflow: 'hidden',
                fontSize: '0.9rem'
              }}
            >
              <thead style={{ backgroundColor: '#f8f9fa' }}>
                <tr>
                  <th className="p-2 align-middle" style={tableHeaderStyle}>
                    Delivery Date
                  </th>
                  <th className="p-2 align-middle" style={tableHeaderStyle}>
                    #TR
                  </th>
                  <th className="p-2 align-middle" style={tableHeaderStyle}>
                    #MR
                  </th>
                  <th className="p-2 align-middle" style={tableHeaderStyle}>
                    Source Warehouse
                  </th>
                  <th className="p-2 align-middle" style={tableHeaderStyle}>
                    Target Warehouse
                  </th>
                  <th className="p-2 align-middle" style={tableHeaderStyle}>
                    Delivery Type
                  </th>
                  <th className="p-2 align-middle" style={tableHeaderStyle}>
                    Driver
                  </th>
                  <th className="p-2 align-middle" style={tableHeaderStyle}>
                    Car
                  </th>
                  <th className="p-2 align-middle" style={tableHeaderStyle}>
                    Action
                  </th>
                </tr>
              </thead>
              <tbody>
                {isLoading && (
                  <tr>
                    <td colSpan={9} className="text-center p-5">
                      <Loader />
                    </td>
                  </tr>
                )}
                {!isLoading && listData.length === 0 && (
                  <RowEmpty colSpan={9} />
                )}
                {!isLoading &&
                  listData.length > 0 &&
                  listData?.map((data) => (
                    <tr key={data?.id}>
                      <td className="p-2 align-middle">
                        {data?.delivery_date
                          ? convertDateWithoutTime(data?.delivery_date)
                          : '-'}
                      </td>
                      <td className="p-2 align-middle">{data?.tr}</td>
                      <td className="p-2 align-middle">{data?.mr}</td>
                      <td className="p-2 align-middle">
                        {data?.src_warehouse}
                      </td>
                      <td className="p-2 align-middle">
                        {data?.target_warehouse}
                      </td>
                      <td className="p-2 align-middle">
                        {firstLetterUpperCase(data?.delivery_type)}
                      </td>
                      <td className="p-2 align-middle">
                        {data?.driver
                          ? firstLetterUpperCase(data?.driver)
                          : '-'}
                      </td>
                      <td className="p-2 align-middle">
                        {data?.plate_no || '-'}
                      </td>
                      <td className="p-2 align-middle">
                        <TableAction>
                          <button
                            className={`btn ${
                              data?.is_created
                                ? 'btn-secondary opacity-25'
                                : 'btn-primary-blue'
                            } btn-sm`}
                            onClick={() => {
                              setCreateMl((prevCreateMl) => ({
                                ...prevCreateMl,
                                mlId: data?.id,
                                code: data?.tr
                              }))
                              setShow(true)
                            }}
                            disabled={data?.is_created}
                            style={{
                              borderRadius: '8px',
                              padding: '6px 16px',
                              fontSize: '0.85rem'
                            }}
                          >
                            + ML DRAFT
                          </button>
                        </TableAction>
                      </td>
                    </tr>
                  ))}
              </tbody>
              <tfoot>
                <tr>
                  <td className="p-3 border-top-0" colSpan={9}>
                    <TablePagination
                      meta={meta}
                      handleOnChangeMeta={handleOnChangeMeta}
                      handlePageChange={handlePageChange}
                    />
                  </td>
                </tr>
              </tfoot>
            </Table>
          </Col>
        </Row>
        <ModalCreate
          show={show}
          onHide={() => setShow(false)}
          createMl={createMl}
          setCreateMl={setCreateMl}
          handleCreateMlDraft={handleCreateMlDraft}
        />
      </Container>
    </>
  )
}

export default ReceiveAtWarehousePlan
